import React, { useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import AccountsUsersService from '../../../services/AccountsUsersService';
import { ACCOUNT_TYPES, ACCOUNT_TIERS } from '../constants';

const EMPTY = {
    pub_name: '', org_name: '', prefix: '', type: 'cus', account_tier: '',
    contract_end: '', metabooks_id: '',
};

// Mirrors the backend admin_generate_prefix algorithm (directory create_ink.php).
// Returns a valid 3-6 char prefix, or '' so the backend auto-generates instead.
function generatePrefixFromName(name) {
    const illegalChars = ['.', '-', '_'];
    let cleaned = name;
    illegalChars.forEach((c) => { cleaned = cleaned.split(c).join(''); });
    const parts = cleaned.split(' ').filter((w) => w.length > 0);
    let num = 2;
    while (num <= 6) {
        const prefix = parts.filter((w) => w.length > 3).map((w) => w.slice(0, num)).join('').toUpperCase();
        if (/^[A-Z0-9]{3,6}$/.test(prefix)) {
            return prefix;
        }
        num++;
    }
    return '';
}

export function AccountForm({ initial, isNew, onSubmit, saving, onCancel }) {
    const [form, setForm] = useState(EMPTY);
    const [prefixState, setPrefixState] = useState({ checking: false, available: null, msg: '' });
    const debounceRef = useRef(null);

    useEffect(() => {
        const base = initial || {};
        setForm({
            pub_name: base.pub_name || '',
            org_name: base.org_name || '',
            prefix: base.prefix || '',
            type: base.type || 'cus',
            account_tier: base.account_tier || '',
            contract_end: (base.contract_end && base.contract_end !== '0000-00-00' && base.contract_end !== '9999-12-31') ? base.contract_end : '',
            metabooks_id: base.metabooks_id || '',
        });
        setPrefixState({ checking: false, available: null, msg: '' });
    }, [initial, isNew]);

    const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

    // Debounced prefix availability check (only for new accounts).
    useEffect(() => {
        if (!isNew) return;
        const prefix = form.prefix.trim().toUpperCase();
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (prefix.length < 3 || prefix.length > 6) {
            setPrefixState({ checking: false, available: null, msg: '' });
            return;
        }
        setPrefixState({ checking: true, available: null, msg: '' });
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await AccountsUsersService.validatePrefix(prefix);
                setPrefixState({ checking: false, available: res.available, msg: res.available ? 'Prefix disponible' : 'Este prefix ya está en uso' });
            } catch (e) {
                setPrefixState({ checking: false, available: null, msg: e.message });
            }
        }, 600);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [form.prefix, isNew]);

    // Calculate the prefix automatically once the user finishes typing the name.
    const handleNameBlur = () => {
        if (!isNew) return;
        if (form.prefix.trim() !== '') return; // don't overwrite a manual prefix
        const computed = generatePrefixFromName(form.pub_name.trim());
        if (computed) {
            set('prefix', computed);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            pub_name: form.pub_name.trim(),
            org_name: form.org_name.trim(),
            type: form.type,
            account_tier: form.account_tier,
            contract_end: form.contract_end || '',
            metabooks_id: form.metabooks_id.trim(),
        };
        if (isNew && form.prefix.trim()) {
            payload.prefix = form.prefix.trim().toUpperCase();
        }
        onSubmit(payload);
    };

    return (
        <form onSubmit={handleSubmit} autoComplete="off">
            <div className="row g-3">
                <div className="col-md-6">
                    <label className="form-label text-white-50">Editorial (pub_name) *</label>
                    <input
                        type="text"
                        className="form-control bg-dark text-white border-secondary py-2"
                        value={form.pub_name}
                        disabled={!isNew}
                        onChange={(e) => set('pub_name', e.target.value)}
                        onBlur={handleNameBlur}
                        required
                    />
                </div>
                <div className="col-md-6">
                    <label className="form-label text-white-50">Organización (org_name) *</label>
                    <input
                        type="text"
                        className="form-control bg-dark text-white border-secondary py-2"
                        value={form.org_name}
                        onChange={(e) => set('org_name', e.target.value)}
                        required
                    />
                </div>

                <div className="col-md-4">
                    <label className="form-label text-white-50">Tipo de cuenta *</label>
                    <select className="form-select bg-dark text-white border-secondary py-2" value={form.type} onChange={(e) => set('type', e.target.value)}>
                        {ACCOUNT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                </div>
                <div className="col-md-4">
                    <label className="form-label text-white-50">Tier</label>
                    <select className="form-select bg-dark text-white border-secondary py-2" value={form.account_tier} onChange={(e) => set('account_tier', e.target.value)}>
                        <option value="">Sin tier</option>
                        {ACCOUNT_TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                </div>
                <div className="col-md-4">
                    <label className="form-label text-white-50">Prefix</label>
                    {isNew ? (
                        <>
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control bg-dark text-white border-secondary py-2"
                                    value={form.prefix}
                                    placeholder="Auto (3-6, opcional)"
                                    onChange={(e) => set('prefix', e.target.value.toUpperCase())}
                                    maxLength={6}
                                />
                                <span className="input-group-text bg-dark border-secondary" style={{ minWidth: '40px', justifyContent: 'center' }}>
                                    {prefixState.checking
                                        ? <FontAwesomeIcon icon={faSpinner} spin />
                                        : prefixState.available === true
                                            ? <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                                            : prefixState.available === false
                                                ? <FontAwesomeIcon icon={faTimesCircle} className="text-danger" />
                                                : null}
                                </span>
                            </div>
                            <div style={{ minHeight: '22px' }}>
                                {prefixState.msg && (
                                    <small className={`d-block mt-1 ${prefixState.available ? 'text-success' : 'text-danger'}`}>
                                        <FontAwesomeIcon icon={prefixState.available ? faCheckCircle : faTimesCircle} className="me-1" />
                                        {prefixState.msg}
                                    </small>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <input type="text" className="form-control bg-dark text-white border-secondary py-2" value={form.prefix} disabled />
                            <div style={{ minHeight: '22px' }} />
                        </>
                    )}
                </div>

                <div className="col-md-6">
                    <label className="form-label text-white-50">Fin de contrato</label>
                    <input type="date" className="form-control bg-dark text-white border-secondary py-2" value={form.contract_end} onChange={(e) => set('contract_end', e.target.value)} />
                    <small className="text-white-50 d-block mt-1">Opcional. Si no se agrega, el contrato se considera indefinido.</small>
                </div>
                <div className="col-md-6">
                    <label className="form-label text-white-50">Metabooks ID</label>
                    <input type="text" className="form-control bg-dark text-white border-secondary py-2" value={form.metabooks_id} onChange={(e) => set('metabooks_id', e.target.value)} />
                </div>
            </div>

            <div className="d-flex justify-content-end gap-3 mt-4">
                <button type="button" className="btn btn-outline-secondary px-4 rounded-pill" onClick={onCancel} disabled={saving}>Cancelar</button>
                <button type="submit" className="btn btn-success px-4 rounded-pill" disabled={saving}>
                    {saving && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                    {isNew ? 'Crear cuenta' : 'Guardar cambios'}
                </button>
            </div>
        </form>
    );
}

export default AccountForm;